"use client"

import { useContext, useEffect, useMemo, useState } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import EditIcon from "@mui/icons-material/Edit"
import WorkHistoryIcon from "@mui/icons-material/WorkHistory"
import BadgeIcon from "@mui/icons-material/Badge"
import BusinessIcon from "@mui/icons-material/Business"
import ApartmentIcon from "@mui/icons-material/Apartment"
import { ThemeProvider } from "@mui/material/styles"
import { ErpContext } from "../../context/ErpContext.js"
import CustomPagination from "../transaccionComponent/CustomPagination"
import ModalRrhhEmpleado from "./ModalRrhhEmpleado.js"
import ModalRrhhContrato from "./ModalRrhhContrato.js"
import {
  RRHH_ESTADOS_LABORALES,
  extractErrorMessage,
  formatFecha,
  getEmpleadoNombreCompleto,
  getEstadoLaboralMeta,
  getSupervisorNombre,
  rrhhTheme,
} from "./rrhhUi.js"

const PAGE_SIZE = 10

const TablaRrhhEmpleadosComponent = () => {
  const {
    negocios,
    ubicaciones,
    getNegocio,
    getUbicacion,
    getRrhhEmpleados,
    getRrhhEmpleadoById,
    postRrhhEmpleado,
    updateRrhhEmpleado,
    postRrhhContratoEmpleado,
  } = useContext(ErpContext)

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  const [searchText, setSearchText] = useState("")
  const [estadoFilter, setEstadoFilter] = useState(null)
  const [negocioFilter, setNegocioFilter] = useState(null)
  const [ubicacionFilter, setUbicacionFilter] = useState(null)

  const [supervisorOptions, setSupervisorOptions] = useState([])
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [empleadoModalOpen, setEmpleadoModalOpen] = useState(false)
  const [empleadoModalMode, setEmpleadoModalMode] = useState("create")
  const [selectedEmpleadoDetail, setSelectedEmpleadoDetail] = useState(null)

  const [contratoModalOpen, setContratoModalOpen] = useState(false)

  const loadCatalogos = async () => {
    await Promise.all([
      negocios.length === 0 ? getNegocio() : Promise.resolve(negocios),
      ubicaciones.length === 0 ? getUbicacion() : Promise.resolve(ubicaciones),
    ])
  }

  const loadSupervisorOptions = async () => {
    try {
      const response = await getRrhhEmpleados({ page: 1, limit: 200, estadoLaboral: "ACTIVO" })
      setSupervisorOptions(response?.data || [])
    } catch (loadError) {
      console.error("Error cargando supervisores RRHH:", loadError)
    }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const response = await getRrhhEmpleados({
        page,
        limit: PAGE_SIZE,
        search: searchText.trim() || undefined,
        estadoLaboral: estadoFilter?.value || undefined,
        idNegocio: negocioFilter?.id || undefined,
        idUbicacionBase: ubicacionFilter?.id || undefined,
      })

      setRows(response?.data || [])
      setTotalPages(response?.totalPages || 1)
      setTotalCount(response?.total || 0)
      setError(null)
    } catch (loadError) {
      setError(extractErrorMessage(loadError, "No se pudieron cargar los empleados RRHH."))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCatalogos()
    loadSupervisorOptions()
  }, [])

  useEffect(() => {
    loadData()
  }, [page, searchText, estadoFilter, negocioFilter, ubicacionFilter])

  const resetFeedback = () => {
    setError(null)
    setSuccess(null)
  }

  const handleRefresh = async () => {
    resetFeedback()
    await Promise.all([loadData(), loadSupervisorOptions()])
  }

  const handleOpenCreate = () => {
    resetFeedback()
    setSelectedEmpleadoDetail(null)
    setEmpleadoModalMode("create")
    setEmpleadoModalOpen(true)
  }

  const handleOpenEdit = async (idEntidad) => {
    resetFeedback()
    setLoadingDetail(true)
    setEmpleadoModalMode("edit")
    setEmpleadoModalOpen(true)

    try {
      const detail = await getRrhhEmpleadoById(idEntidad)
      setSelectedEmpleadoDetail(detail)
    } catch (detailError) {
      setError(extractErrorMessage(detailError, "No se pudo cargar el empleado seleccionado."))
      setEmpleadoModalOpen(false)
    } finally {
      setLoadingDetail(false)
    }
  }

  const ensureEmpleadoDetail = async (idEntidad) => {
    if (selectedEmpleadoDetail?.empleado?.idEntidad === idEntidad) {
      return selectedEmpleadoDetail
    }

    const detail = await getRrhhEmpleadoById(idEntidad)
    setSelectedEmpleadoDetail(detail)
    return detail
  }

  const handleOpenContrato = async (idEntidad) => {
    resetFeedback()
    setLoadingDetail(true)
    try {
      await ensureEmpleadoDetail(idEntidad)
      setContratoModalOpen(true)
    } catch (detailError) {
      setError(extractErrorMessage(detailError, "No se pudo cargar el empleado para contrato."))
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleSaveEmpleado = async (payload) => {
    if (empleadoModalMode === "edit") {
      await updateRrhhEmpleado(selectedEmpleadoDetail.empleado.idEntidad, payload)
      setSuccess("Empleado actualizado correctamente.")
    } else {
      await postRrhhEmpleado(payload)
      setSuccess("Empleado creado correctamente.")
    }

    setEmpleadoModalOpen(false)
    await Promise.all([loadData(), loadSupervisorOptions()])
  }

  const handleSaveContrato = async (payload) => {
    const idEntidad = selectedEmpleadoDetail?.empleado?.idEntidad
    if (!idEntidad) return

    await postRrhhContratoEmpleado(idEntidad, payload)
    setSuccess("Contrato registrado correctamente.")
    setContratoModalOpen(false)

    const refreshedDetail = await getRrhhEmpleadoById(idEntidad)
    setSelectedEmpleadoDetail(refreshedDetail)
    await loadData()
  }

  const pageActiveCount = useMemo(
    () => rows.filter((row) => String(row.estadoLaboral || "").toUpperCase() === "ACTIVO").length,
    [rows]
  )

  const columns = [
    {
      field: "legajo",
      headerName: "Legajo",
      minWidth: 120,
      flex: 0.7,
    },
    {
      field: "empleado",
      headerName: "Empleado",
      minWidth: 220,
      flex: 1.4,
      valueGetter: (_, row) => getEmpleadoNombreCompleto(row),
    },
    {
      field: "dniCuitCuil",
      headerName: "DNI / CUIL",
      minWidth: 140,
      flex: 0.9,
      valueGetter: (_, row) => row.entidad?.dniCuitCuil || "-",
    },
    {
      field: "estadoLaboral",
      headerName: "Estado",
      minWidth: 130,
      flex: 0.8,
      renderCell: ({ value }) => {
        const meta = getEstadoLaboralMeta(value)
        return (
          <Chip
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: meta.backgroundColor,
              color: meta.color,
              fontWeight: 700,
            }}
          />
        )
      },
    },
    {
      field: "fechaIngreso",
      headerName: "Ingreso",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatFecha(value),
    },
    {
      field: "negocio",
      headerName: "Negocio",
      minWidth: 160,
      flex: 1,
      valueGetter: (_, row) => row.negocio?.descripcion || "-",
    },
    {
      field: "ubicacionBase",
      headerName: "Sucursal",
      minWidth: 160,
      flex: 1,
      valueGetter: (_, row) => row.ubicacionBase?.descripcion || "-",
    },
    {
      field: "supervisor",
      headerName: "Supervisor",
      minWidth: 190,
      flex: 1.1,
      valueGetter: (_, row) => getSupervisorNombre(row.supervisor),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar empleado">
            <IconButton size="small" onClick={() => handleOpenEdit(row.idEntidad)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Agregar contrato">
            <IconButton size="small" onClick={() => handleOpenContrato(row.idEntidad)}>
              <WorkHistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              border: "1px solid rgba(15, 23, 42, 0.08)",
              background: "linear-gradient(135deg, #ffffff 0%, #fffaf0 100%)",
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#1f2937" }}>
                  Empleados RRHH
                </Typography>
                <Typography variant="body1" sx={{ color: "#4b5563", mt: 0.5 }}>
                  Padrón laboral, alta y edición de empleados con ficha RRHH.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                <Chip icon={<BadgeIcon />} label={`${totalCount} empleados`} variant="outlined" />
                <Chip
                  icon={<BusinessIcon />}
                  label={`${pageActiveCount} activos en página`}
                  sx={{
                    backgroundColor: alpha("#2e7d32", 0.08),
                    color: "#2e7d32",
                  }}
                />
                <Chip icon={<ApartmentIcon />} label={`Página ${page} de ${totalPages}`} variant="outlined" />
              </Stack>
            </Stack>
          </Paper>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          <Paper elevation={0} sx={{ p: 2, border: "1px solid rgba(15, 23, 42, 0.08)" }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", xl: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", xl: "center" }}
                justifyContent="space-between"
              >
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ flex: 1 }}>
                  <TextField
                    placeholder="Buscar por legajo, nombre, apellido o DNI"
                    value={searchText}
                    onChange={(event) => {
                      setPage(1)
                      setSearchText(event.target.value)
                    }}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Autocomplete
                    options={RRHH_ESTADOS_LABORALES}
                    value={estadoFilter}
                    onChange={(_, value) => {
                      setPage(1)
                      setEstadoFilter(value)
                    }}
                    sx={{ minWidth: 180 }}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => <TextField {...params} label="Estado" />}
                  />

                  <Autocomplete
                    options={negocios}
                    value={negocioFilter}
                    onChange={(_, value) => {
                      setPage(1)
                      setNegocioFilter(value)
                    }}
                    sx={{ minWidth: 220 }}
                    getOptionLabel={(option) => option.descripcion || ""}
                    renderInput={(params) => <TextField {...params} label="Negocio" />}
                  />

                  <Autocomplete
                    options={ubicaciones}
                    value={ubicacionFilter}
                    onChange={(_, value) => {
                      setPage(1)
                      setUbicacionFilter(value)
                    }}
                    sx={{ minWidth: 220 }}
                    getOptionLabel={(option) => option.descripcion || ""}
                    renderInput={(params) => <TextField {...params} label="Sucursal" />}
                  />
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                    Actualizar
                  </Button>
                  <Button variant="contained" startIcon={<AddCircleIcon />} onClick={handleOpenCreate}>
                    Nuevo empleado
                  </Button>
                </Stack>
              </Stack>

              <Box sx={{ width: "100%" }}>
                <DataGrid
                  autoHeight
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row.idEntidad}
                  loading={loading}
                  disableRowSelectionOnClick
                  hideFooter
                  rowHeight={52}
                  onRowDoubleClick={(params) => handleOpenEdit(params.row.idEntidad)}
                />
              </Box>

              <CustomPagination
                currentPage={page}
                totalPages={totalPages}
                totalCount={totalCount}
                vieneDe="tablaRrhhEmpleados"
                onPageChange={setPage}
              />
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <ModalRrhhEmpleado
        open={empleadoModalOpen}
        mode={empleadoModalMode}
        empleadoDetail={selectedEmpleadoDetail}
        loading={loadingDetail}
        supervisorOptions={supervisorOptions}
        negocios={negocios}
        ubicaciones={ubicaciones}
        onClose={() => setEmpleadoModalOpen(false)}
        onSave={handleSaveEmpleado}
        onOpenContrato={() => {
          setEmpleadoModalOpen(false)
          setContratoModalOpen(true)
        }}
      />

      <ModalRrhhContrato
        open={contratoModalOpen}
        empleadoNombre={getEmpleadoNombreCompleto(selectedEmpleadoDetail?.empleado)}
        contratoVigente={selectedEmpleadoDetail?.contratoVigente}
        onClose={() => setContratoModalOpen(false)}
        onSave={handleSaveContrato}
      />
    </ThemeProvider>
  )
}

export default TablaRrhhEmpleadosComponent
